import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import TimeframeSelect from '../shared/TimeframeSelect';
import BottomSheet from '../shared/BottomSheet';
import FilterChip from '../shared/FilterChip';
import SheetOptionList from '../shared/SheetOptionList';
import SheetSection from '../shared/SheetSection';
import { MOBILE_UI } from '../../constants/mobileUi';

export const TIMEFRAME_PRESETS = [
  { id: 'today', label: 'Today' },
  { id: 'week', label: 'Week' },
  { id: 'month', label: 'Month' },
  { id: 'quarter', label: 'Quarter' },
  { id: 'ytd', label: 'YTD' },
  { id: 'year', label: '1Y' },
  { id: '2y', label: '2Y' },
  { id: '5y', label: '5Y' },
  { id: 'all', label: 'All' },
];

/** Preset labels restated as a period, for sections that need to declare their own scope. */
const TIMEFRAME_SCOPE_LABELS = {
  today: 'Today',
  week: 'This week',
  month: 'This month',
  quarter: 'This quarter',
  ytd: 'Year to date',
  year: 'This year',
  '2y': 'Last 2 years',
  '5y': 'Last 5 years',
  all: 'All time',
};

export function getTimeframeScopeLabel(timeframe, dateRange) {
  if (dateRange?.since && dateRange?.until) return 'Custom range';
  return TIMEFRAME_SCOPE_LABELS[timeframe] || null;
}

function formatDateInput(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function isValidRange(since, until) {
  if (!since || !until) return false;
  return since <= until;
}

/**
 * Mobile timeframe control: a chip that opens a sheet of presets, with the custom
 * range revealed in the same sheet. Nine presets are too many for an inline strip
 * and a scroll-wheel picker costs two gestures per change.
 */
function MobileTimeframeControl({
  isMobile,
  selectValue,
  selectOptions,
  onSelectChange,
  alignEnd,
  footer,
  colors,
  allowCustom,
  draftSince,
  draftUntil,
  setDraftSince,
  setDraftUntil,
  applyCustom,
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const customRef = useRef(null);
  const activeLabel = selectOptions.find((o) => o.value === selectValue)?.label || 'Timeframe';
  const showCustomFields = allowCustom && selectValue === 'custom';

  // The preset list is taller than the sheet, so reveal the date fields rather than
  // leaving them below the fold after Custom is picked.
  useEffect(() => {
    if (!open || !showCustomFields) return;
    customRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [open, showCustomFields]);

  useEffect(() => {
    if (!open || isMobile) return undefined;
    const onPointerDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) setOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isMobile, open]);

  const dateInputStyle = {
    flex: '1 1 0',
    minWidth: 0,
    height: MOBILE_UI.controlHeight,
    padding: '0 10px',
    borderRadius: MOBILE_UI.radiusControl,
    border: `1px solid ${colors.border}`,
    background: colors.bgInput || colors.bgSecondary,
    color: colors.text,
    fontSize: MOBILE_UI.font.body,
    fontWeight: 600,
    boxSizing: 'border-box',
  };

  const pickerContent = (
    <>
      <SheetOptionList
        options={selectOptions}
        value={selectValue}
        onChange={(next) => {
          onSelectChange(next);
          // 'custom' reveals the date fields below instead of closing.
          if (next !== 'custom') setOpen(false);
        }}
        ariaLabel="Timeframe"
        getTestId={(option) => `performance-timeframe-${option.value}`}
      />

      {showCustomFields ? (
        <div ref={customRef}>
          <SheetSection title="Custom range">
            <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
              <input
                type="date"
                value={draftSince}
                onChange={(e) => setDraftSince(e.target.value)}
                style={dateInputStyle}
                aria-label="Start date"
              />
              <input
                type="date"
                value={draftUntil}
                onChange={(e) => setDraftUntil(e.target.value)}
                style={dateInputStyle}
                aria-label="End date"
              />
            </div>
            <Button
              variant="primary"
              onClick={() => {
                applyCustom();
                setOpen(false);
              }}
              disabled={!isValidRange(draftSince, draftUntil)}
              data-testid="performance-timeframe-apply-custom"
              style={{
                width: '100%',
                height: MOBILE_UI.controlHeight,
                borderRadius: MOBILE_UI.radiusControl,
                fontWeight: 700,
              }}
            >
              Apply range
            </Button>
          </SheetSection>
        </div>
      ) : null}
    </>
  );

  return (
    <div
      ref={rootRef}
      style={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: alignEnd ? 'flex-end' : 'flex-start',
        gap: 6,
        minWidth: 0,
      }}
    >
      <FilterChip
        testId="performance-timeframe-select"
        ariaLabel="Timeframe"
        ariaExpanded={open}
        value={activeLabel}
        active={open}
        onClick={() => setOpen(true)}
      />
      {footer}

      {isMobile ? (
        <BottomSheet
          show={open}
          onHide={() => setOpen(false)}
          title="Timeframe"
          testId="performance-timeframe-sheet"
          closeLabel="Close timeframe picker"
        >
          {pickerContent}
        </BottomSheet>
      ) : open ? (
        <div
          role="dialog"
          aria-label="Timeframe"
          data-testid="performance-timeframe-sheet"
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            right: alignEnd ? 0 : undefined,
            left: alignEnd ? undefined : 0,
            zIndex: 45,
            width: 300,
            maxHeight: 'min(70vh, 620px)',
            overflowY: 'auto',
            padding: '8px 14px',
            border: `1px solid ${colors.border}`,
            borderRadius: 12,
            backgroundColor: colors.bgPrimary || colors.bgCard,
            boxShadow: '0 12px 32px rgba(0, 0, 0, 0.35)',
          }}
        >
          {pickerContent}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Compact timeframe dropdown + optional custom date range.
 * onChange({ timeframe, dateRange: { since, until } | null })
 *
 * @param {string} [align='left'] - 'left' | 'right'
 * @param {boolean} [showCustom=true] - include Custom range option
 * @param {Array<{id:string,label:string}>} [presets] - defaults to TIMEFRAME_PRESETS
 * @param {React.ReactNode} [footer] - optional row below (e.g. date range label)
 * @param {boolean} [compact=false] - tighter select for chart headers
 * @param {boolean} [emphasis=false] - larger, higher-contrast control for primary page filters
 */
const PerformanceTimeframeBar = ({
  timeframe,
  dateRange,
  onChange,
  colors,
  isMobile,
  align = 'left',
  showCustom: allowCustom = true,
  presets = TIMEFRAME_PRESETS,
  footer = null,
  compact = false,
  emphasis = false,
  desktopChip = false,
}) => {
  const isCustom = !!dateRange?.since && !!dateRange?.until;
  const [showCustom, setShowCustom] = useState(isCustom);
  const [draftSince, setDraftSince] = useState(dateRange?.since || '');
  const [draftUntil, setDraftUntil] = useState(dateRange?.until || '');

  useEffect(() => {
    if (isCustom) {
      setDraftSince(dateRange.since);
      setDraftUntil(dateRange.until);
      setShowCustom(true);
    }
  }, [dateRange?.since, dateRange?.until, isCustom]);

  const applyPreset = useCallback(
    (id) => {
      setShowCustom(false);
      onChange({ timeframe: id, dateRange: null });
    },
    [onChange]
  );

  const applyCustom = useCallback(() => {
    if (!isValidRange(draftSince, draftUntil)) return;
    onChange({
      timeframe: 'custom',
      dateRange: { since: draftSince, until: draftUntil },
    });
  }, [draftSince, draftUntil, onChange]);

  const onSelectChange = useCallback(
    (id) => {
      if (id === 'custom') {
        const end = new Date();
        const start = new Date();
        start.setDate(end.getDate() - 6);
        setDraftSince(dateRange?.since || formatDateInput(start));
        setDraftUntil(dateRange?.until || formatDateInput(end));
        setShowCustom(true);
        return;
      }
      applyPreset(id);
    },
    [applyPreset, dateRange?.since, dateRange?.until]
  );

  const selectValue = isCustom || showCustom ? 'custom' : timeframe;

  const selectOptions = useMemo(() => {
    const opts = presets.map((t) => ({ value: t.id, label: t.label }));
    if (allowCustom) opts.push({ value: 'custom', label: 'Custom' });
    return opts;
  }, [presets, allowCustom]);

  const selectSize = emphasis ? 'emphasis' : compact ? 'compact' : 'default';
  const selectWidth = emphasis
    ? (isMobile ? '100%' : 148)
    : compact
      ? 110
      : isMobile
        ? 128
        : 140;
  const selectMinWidth = emphasis
    ? (isMobile ? 140 : 148)
    : compact
      ? 96
      : 120;

  const inputStyle = {
    fontSize: isMobile ? '0.75rem' : '0.8rem',
    padding: '4px 8px',
    borderRadius: '6px',
    border: `1px solid ${colors.border}`,
    background: colors.bgSecondary,
    color: colors.text,
    height: 32,
    lineHeight: '22px',
    boxSizing: 'border-box',
    width: isMobile ? 132 : 140,
    minWidth: 0,
    maxWidth: '100%',
    flex: '0 0 auto',
  };

  const alignEnd = align === 'right';

  if (isMobile || desktopChip) {
    return (
      <MobileTimeframeControl
        isMobile={isMobile}
        selectValue={selectValue}
        selectOptions={selectOptions}
        onSelectChange={onSelectChange}
        alignEnd={alignEnd}
        footer={footer}
        colors={colors}
        allowCustom={allowCustom}
        draftSince={draftSince}
        draftUntil={draftUntil}
        setDraftSince={setDraftSince}
        setDraftUntil={setDraftUntil}
        applyCustom={applyCustom}
      />
    );
  }

  return (
    <div
      style={{
        marginBottom: 0,
        width: alignEnd ? 'auto' : '100%',
        maxWidth: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: alignEnd ? 'flex-end' : 'flex-start',
        minWidth: 0,
      }}
    >
      <TimeframeSelect
        value={selectValue}
        options={selectOptions}
        onChange={onSelectChange}
        size={selectSize}
        width={selectWidth}
        minWidth={selectMinWidth}
        testId="performance-timeframe-select"
      />

      {footer ? (
        <div
          style={{
            width: '100%',
            display: 'flex',
            justifyContent: alignEnd ? 'flex-end' : 'flex-start',
            marginTop: 6,
          }}
        >
          {footer}
        </div>
      ) : null}

      {allowCustom && showCustom && (
        <div
          style={{
            display: 'flex',
            flexWrap: isMobile ? 'wrap' : 'nowrap',
            alignItems: 'center',
            justifyContent: alignEnd ? 'flex-end' : 'flex-start',
            gap: isMobile ? '8px' : '10px',
            marginTop: '10px',
            padding: isMobile ? '8px 10px' : '8px 12px',
            borderRadius: '8px',
            border: `1px solid ${colors.border}`,
            background: colors.bgCard,
            width: '100%',
            maxWidth: alignEnd ? 480 : undefined,
          }}
        >
          <label
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 6,
              margin: 0,
              flex: '0 0 auto',
            }}
          >
            <span
              style={{
                fontSize: '0.7rem',
                color: colors.textMuted,
                textTransform: 'uppercase',
                letterSpacing: '0.02em',
                whiteSpace: 'nowrap',
              }}
            >
              From
            </span>
            <input
              type="date"
              value={draftSince}
              onChange={(e) => setDraftSince(e.target.value)}
              style={inputStyle}
              aria-label="Start date"
            />
          </label>
          <label
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 6,
              margin: 0,
              flex: '0 0 auto',
            }}
          >
            <span
              style={{
                fontSize: '0.7rem',
                color: colors.textMuted,
                textTransform: 'uppercase',
                letterSpacing: '0.02em',
                whiteSpace: 'nowrap',
              }}
            >
              To
            </span>
            <input
              type="date"
              value={draftUntil}
              onChange={(e) => setDraftUntil(e.target.value)}
              style={inputStyle}
              aria-label="End date"
            />
          </label>
          <Button
            variant="primary"
            size="sm"
            onClick={applyCustom}
            disabled={!isValidRange(draftSince, draftUntil)}
            style={{
              flexShrink: 0,
              height: 32,
              padding: '0 12px',
              display: 'inline-flex',
              alignItems: 'center',
              lineHeight: 1,
            }}
          >
            Apply
          </Button>
        </div>
      )}
    </div>
  );
};

export default PerformanceTimeframeBar;
